import axios from "axios";
import useAuthCandidateToken from "./useAuthCandidateToken";
import BASE from "../config";
import useAuthConsultantToken from "./useAuthConsultantToken";
import { useHistory } from "react-router-dom";
import {
  clearUserAuthData,
} from "../services/authRefresh";

const getUserLogoutUrl = (location) => {
  const currentPath = location.pathname;
  const currentSearch = location.search;
  const normalizedPathname = currentPath.replace(/\/+$/, "") || "/";

  if (normalizedPathname === "/careers") return "/careers";
  if (normalizedPathname.startsWith("/careers/")) {
    return `/careers?continueTo=${currentPath}${currentSearch}`;
  }

  if (normalizedPathname === "/consultant") return "/consultant";
  if (normalizedPathname.startsWith("/consultant/")) {
    return `/consultant?continueTo=${currentPath}${currentSearch}`;
  }

  return `/?continueTo=${currentPath}${currentSearch}`;
};

const useAxios = () => {
  const { candidateToken } = useAuthCandidateToken();
  const { consultantToken } = useAuthConsultantToken();
  const history = useHistory();

  const logoutUser = () => {
    clearUserAuthData();
    window.location.replace(getUserLogoutUrl(history.location));
  };

  const logoutCandidate = () => {
    localStorage.removeItem("candidate.token");
    localStorage.removeItem("candidate.id");
    localStorage.removeItem("candidate.name");
    localStorage.removeItem("candidate.email");
    localStorage.removeItem("candidate.role");
    window.location.replace(
      `/careers?continueTo=${history.location.pathname}${history.location.search}`,
    );
  };

  const logoutConsultant = () => {
    localStorage.removeItem("consultant.token");
    localStorage.removeItem("consultant.id");
    localStorage.removeItem("consultant.name");
    localStorage.removeItem("consultant.email");
    localStorage.removeItem("consultant.role");
    window.location.replace(
      `/consultant?continueTo=${history.location.pathname}${history.location.search}`,
    );
  };

  const axiosInstance = axios.create({
    baseURL: BASE.api,
    headers: {
      "Content-Type": "application/json",
    },
  });

  axiosInstance.interceptors.request.use(
    (request) => {
      if (request.headers) {
        if (request.tokenType === "user") {
          request.headers.Authorization = `Bearer ${localStorage.getItem("user.token")}`;
        }

        if (request.tokenType === "candidate") {
          request.headers.Authorization = `Bearer ${candidateToken}`;
        }

        if (request.tokenType === "consultant") {
          request.headers.Authorization = `Bearer ${consultantToken}`;
        }
      }
      return request;
    },
    (error) => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      const originalRequest = error.config;
      const res = error?.response;
      const tokenType = originalRequest?.tokenType;
      const isRefreshRequest = originalRequest?.url?.includes("/user/keycloak/refresh");

      // With local JWT (24h), a 401 means the session is genuinely invalid.
      // No need to try Keycloak refresh — just logout.
      // SSO session validation is handled separately via proactive refresh in isTokenValid.
      if (res?.status === 401 && tokenType === "user" && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        logoutUser();
        return Promise.reject(error);
      }

      // [LEGACY] Logout on token error for other types OR if refresh logic was skipped
      if (
        res?.status === 401 ||
        res?.data?.error?.name === "TokenExpiredError" ||
        res?.data?.error?.message === "jwt expired" ||
        res?.data?.error?.name === "JsonWebTokenError" ||
        res?.data?.error?.message === "jwt malformed"
      ) {
        if (tokenType === "user") {
          logoutUser();
        }

        if (tokenType === "candidate") {
          logoutCandidate();
        }

        if (tokenType === "consultant") {
          logoutConsultant();
        }
      }

      return Promise.reject(error);
    },
  );

  return axiosInstance;
};

export default useAxios;
